<template>
    <div
            :id="`home-compare-chart-${comparison.feature_id}`"
            class="home-compare-chart tw-flex tw-flex-col tw-justify-center tw-items-center tw-self-stretch
                    tw-p-5 lg:tw-px-8 lg:tw-justify-start"
    >
        <span
                class="tw-self-stretch tw-justify-start tw-text-darkGrayV2 tw-text-sm tw-font-bold tw-font-openSans
                     tw-leading-tight tw-mb-3"
        >
            {{ title }}
        </span>
        <div class="home-compare-chart-content tw-flex tw-flex-col tw-items-start tw-gap-2 tw-self-stretch">
            <b-skeleton
                    v-if="loading"
                    class="home-compare-chart-loading"
                    height="100px"
            />
            <highcharts
                    v-if="displayChart"
                    :options="options"
                    class="home-compare-chart__highchart"
            />
        </div>
    </div>
</template>
<script>
import './HomeCompareChart.scss';
import IsMobileMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IsMobileMixin";

const MAX_GROUPS = 5;
const POINT_WIDTH = 20;
const POINT_PADDING = 4;

export default {
    name: "HomeCompareChart",
    components: {
        highcharts: () => import('highcharts-vue').then(module => module.Chart),
    },
    mixins: [
        IsMobileMixin,
    ],
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        comparison: {
            type: Object,
            required: true,
        },
        comparisonDescriptions: {
            type: Array,
            required: true,
        },
        customWidth: {
            type: Number,
            default: 0,
        },
        widestBarFactor: {
            type: Number,
            default: 110,
        },
    },
    computed: {
        displayChart() {
            return !this.loading && this.comparison?.groups;
        },
        title() {
            return this.comparisonDescriptions.find(description => description.feature_id === this.comparison.feature_id).title;
        },
        chartHeight() {
            //set chart height depending on number of groups and padding + up and down padding
            return this.seriesData.length * (POINT_WIDTH + POINT_PADDING) + 20;
        },
        seriesData() {
            if (!this.comparison?.groups) {
                return [];
            }

            const comparisonGroups = this.reduceGroupsIfNeeded(this.comparison.groups);
            const subjectGroupIndex = this.adjustSubjectGroupIndex();

            return this.mapComparisonGroupsToChartData(comparisonGroups, subjectGroupIndex);
        },
        maxYAxis() {
            const maxData = Math.max(...this.seriesData.map(o => o.y));
            return maxData + (maxData * this.widestBarFactor / this.maxWidth);
        },
        maxWidth() {
            return this.customWidth ? this.customWidth : this.isMobile ? 295 : 336;
        },
        options() {
            if (this.loading) {
                return {};
            }

            const seriesData = this.seriesData;
            const chartHeight = this.chartHeight;
            const maxWidth = this.maxWidth;

            return {
                chart: {
                    type: 'bar',
                    backgroundColor: null,
                    borderWidth: 0,
                    width: maxWidth,
                    height: chartHeight,
                    spacingTop: 0,
                    spacingBottom: 0,
                    style: {
                        fontFamily: 'Open Sans, sans-serif',
                        overflow: 'visible'
                    },
                    inside: false,
                },
                title: {
                    text: null,
                },
                tooltip: {
                    enabled: false
                },
                legend: {
                    enabled: false
                },
                xAxis: {
                    lineWidth: 0,
                    minorGridLineWidth: 0,
                    minorTickLength: 0,
                    tickLength: 0,
                    allowDecimals: false,
                    tickInterval: 1,
                    labels: {
                        useHtml: false,
                        x: -5,
                        style: {
                            color: '#3A3A3A',
                            fontSize: '14px',
                            fontFamily: 'Open Sans, sans-serif',
                            fontWeight: '600',
                        },
                        formatter: function () {
                            const self = this;
                            return seriesData[self.value].name;
                        }
                    },
                },
                yAxis: {
                    visible: false,
                    max: this.maxYAxis,
                    endOnTick: false,
                },
                plotOptions: {
                    bar: {
                        className: 'home-compare-chart__highchart-bar',
                        borderRadius: 5,
                        dataLabels: {
                            enabled: true,
                            useHTML: true,
                            inside: false,
                            formatter: function () {
                                const self = this;
                                let percent = seriesData[self.x].percent;
                                let value = seriesData[self.x].y.toLocaleString();
                                return `<span style="margin-left:5px;
                                                font-family: Open Sans, sans-serif;
                                                font-size: 14px;
                                                font-style: normal;
                                                font-weight: 400;
                                                line-height: 20px;
                                                text-overflow: ellipsis;"
                                        >
                                                <b style="font-weight: 700;">${percent}</b>
                                                <b style="font-weight: 400;">&nbsp(${value})</b>
                                        </span>`;
                            },
                        },
                    },
                    series: {
                        color: '#E4E4E6',
                    }
                },
                credits: {
                    enabled: false
                },
                series: [{
                    type: "bar",
                    pointWidth: POINT_WIDTH,
                    data: this.seriesData,
                    enableMouseTracking: false,
                }],
                exporting: {
                    enabled: false
                },
            };
        }
    },
    methods: {
        mapComparisonGroupsToChartData(groups, subjectGroupIndex) {
            return groups.map((item, index) => {
                let percent = item.proportion * 100;
                percent = percent < 1 ? '<1' : percent.toFixed(0);

                return {
                    name: this.adjustHighestValue(item.group),
                    y: item.count,
                    percent: percent + '%',
                    className: subjectGroupIndex === index ? 'primary-color' : '',
                };
            });
        },

        adjustHighestValue(value) {
            return value.replace(' or more', '+');
        },

        adjustSubjectGroupIndex() {
            const [startIndex, endIndex] = this.findMiddleValues(
                this.comparison.subject_group_index,
                this.comparison.groups.length - 1
            );
            let subjectGroupIndex = this.comparison.subject_group_index;
            if (subjectGroupIndex > endIndex) {
                subjectGroupIndex = MAX_GROUPS - 1;
            }
            if (subjectGroupIndex <= startIndex) {
                subjectGroupIndex = 0;
            }
            return subjectGroupIndex - startIndex;
        },

        reduceGroupsIfNeeded() {
            const groups = this.comparison.groups;
            if (groups.length <= MAX_GROUPS) {
                return groups;
            }

            const subjectGroupIndex = this.comparison.subject_group_index;

            const [startIndex, endIndex] = this.findMiddleValues(subjectGroupIndex, groups.length - 1);
            let newGroups = groups.slice(startIndex, endIndex + 1);

            newGroups = this.handleLessOtherGroups(startIndex, newGroups);
            newGroups = this.handleMoreOtherGroups(endIndex, newGroups);

            return newGroups;
        },

        findMiddleValues(valueOfInterest, totalValues) {
            let start, end;

            if (valueOfInterest <= 2) {
                start = 0;
                end = 4;
            } else if (valueOfInterest >= totalValues - 2) {
                start = totalValues - 4;
                end = totalValues;
            } else {
                start = valueOfInterest - 2;
                end = valueOfInterest + 2;
            }

            return [start, end];
        },

        handleLessOtherGroups(startIndex, newGroups) {
            const groups = this.comparison.groups;
            const lessOtherGroups = groups.slice(0, startIndex + 1);
            if (lessOtherGroups.length <= 1) return newGroups;

            const lessOtherCount = lessOtherGroups.reduce((acc, group) => acc + group.count, 0);
            const lessOtherPercent = lessOtherGroups.reduce((acc, group) => acc + parseFloat(group.proportion), 0);

            let identifier = '-';
            switch (this.comparison.feature_id) {
                case 'bedrooms_count':
                case 'bathrooms_count':
                    identifier = ' or less';
                    break;
                case 'structure_built_year':
                    identifier = ' and earlier';
                    break;
            }

            const limitGroupName = groups[startIndex].group + identifier;

            return [
                {
                    group: limitGroupName,
                    count: lessOtherCount,
                    proportion: lessOtherPercent,
                },
                ...newGroups.slice(1, MAX_GROUPS)
            ];
        },

        handleMoreOtherGroups(endIndex, newGroups) {
            const groups = this.comparison.groups;
            const moreOtherGroups = groups.slice(endIndex, groups.length);
            if (moreOtherGroups.length <= 1) return newGroups;

            const moreOtherCount = moreOtherGroups.reduce((acc, group) => acc + group.count, 0);
            const moreOtherPercent = moreOtherGroups.reduce((acc, group) => acc + parseFloat(group.proportion), 0);

            const limitGroupName = groups[endIndex].group + '+';

            return [
                ...newGroups.slice(0, MAX_GROUPS - 1),
                {
                    group: limitGroupName,
                    count: moreOtherCount,
                    proportion: moreOtherPercent,
                }
            ];
        }
    }
};
</script>
